import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown, Loader2 } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';

// Organized by region for compact display
const languageGroups = {
  'Major': [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'uk', name: 'Ukrainian', flag: '🇺🇦' },
  ],
  'Asian': [
    { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
    { code: 'tl', name: 'Filipino', flag: '🇵🇭' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'ur', name: 'Urdu', flag: '🇵🇰' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'th', name: 'Thai', flag: '🇹🇭' },
  ],
  'Middle East': [
    { code: 'fa', name: 'Farsi', flag: '🇮🇷' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'he', name: 'Hebrew', flag: '🇮🇱' },
  ],
  'European': [
    { code: 'ga', name: 'Irish', flag: '🇮🇪' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pl', name: 'Polish', flag: '🇵🇱' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  ],
  'African': [
    { code: 'af', name: 'Afrikaans', flag: '🇿🇦' },
    { code: 'sw', name: 'Swahili', flag: '🇰🇪' },
    { code: 'am', name: 'Amharic', flag: '🇪🇹' },
    { code: 'om', name: 'Oromo', flag: '🇪🇹' },
    { code: 'ti', name: 'Tigrinya', flag: '🇪🇹' },
    { code: 'sid', name: 'Sidamo', flag: '🇪🇹' },
    { code: 'wal', name: 'Wolaytta', flag: '🇪🇹' },
    { code: 'gur', name: 'Gurage', flag: '🇪🇹' },
    { code: 'had', name: 'Hadiyya', flag: '🇪🇹' },
    { code: 'gam', name: 'Gamo', flag: '🇪🇹' },
    { code: 'aar', name: 'Afar', flag: '🇪🇹' },
    { code: 'ak', name: 'Twi', flag: '🇬🇭' },
    { code: 'fat', name: 'Fanti', flag: '🇬🇭' },
    { code: 'dag', name: 'Dagomba', flag: '🇬🇭' },
    { code: 'gua', name: 'Guan', flag: '🇬🇭' },
    { code: 'ha', name: 'Hausa', flag: '🇳🇬' },
    { code: 'ig', name: 'Igbo', flag: '🇳🇬' },
    { code: 'ee', name: 'Ewe', flag: '🇬🇭' },
    { code: 'yo', name: 'Yoruba', flag: '🇳🇬' },
    { code: 'zu', name: 'Zulu', flag: '🇿🇦' },
    { code: 'xh', name: 'Xhosa', flag: '🇿🇦' },
    { code: 'so', name: 'Somali', flag: '🇸🇴' },
  ],
};

// Flat list for lookup
const allLanguages = Object.values(languageGroups).flat();

const LanguageSwitcher = ({ variant = 'dropdown', className = '' }) => {
  const { currentLanguage, changeLanguage, isTranslating, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const currentLang = allLanguages.find(lang => lang.code === currentLanguage) || allLanguages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (langCode) => {
    setIsOpen(false);
    if (langCode !== currentLanguage) {
      await changeLanguage(langCode);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isTranslating}
        className={`flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ea580c] focus:ring-offset-2 transition-colors ${
          isTranslating ? 'opacity-70 cursor-wait' : ''
        }`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Current language: ${currentLang.name}. Click to change language.`}
        data-testid="language-switcher-button"
      >
        {isTranslating ? (
          <Loader2 className="w-4 h-4 text-[#ea580c] animate-spin" aria-hidden="true" />
        ) : (
          <Globe className="w-4 h-4 text-gray-500" aria-hidden="true" />
        )}
        <span className="text-sm font-medium text-gray-700">
          {currentLang.flag} {currentLang.code.toUpperCase()}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div 
          className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden"
          style={{ width: '240px' }}
          role="listbox"
          aria-label="Select language"
        >
          {/* Header */}
          <div className="px-3 py-2 bg-gradient-to-r from-[#ea580c] to-[#f59e0b] text-white">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              <span className="font-semibold text-sm">{t('language.selectLanguage', 'Select Language')}</span>
            </div>
            <p className="text-xs text-orange-100 mt-1">
              {t('language.translateTo', 'Instant translation to your language')}
            </p>
          </div>

          {/* Translation Status */}
          {isTranslating && (
            <div className="px-3 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
              <span className="text-xs text-blue-700">{t('language.translating', 'Translating page...')}</span>
            </div>
          )}

          {/* Language List */}
          <div className="max-h-[350px] overflow-y-auto">
            {Object.entries(languageGroups).map(([region, langs]) => (
              <div key={region}>
                <div className="sticky top-0 bg-gray-100 px-3 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  {region}
                </div>
                {langs.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    disabled={isTranslating}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors border-b border-gray-50 ${
                      currentLanguage === lang.code
                        ? 'bg-[#ea580c] text-white'
                        : 'hover:bg-orange-50 text-gray-700'
                    } ${isTranslating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="option"
                    aria-selected={currentLanguage === lang.code}
                    data-testid={`language-option-${lang.code}`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="text-sm font-medium flex-1">{lang.name}</span>
                    {currentLanguage === lang.code && (
                      <Check className="w-4 h-4" />
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Footer with info */}
          <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
            <p className="text-[10px] text-gray-500 text-center">
              Powered by AI Translation
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
